import { useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { fetchScoreCalculatorTypes } from '../api/services/qualityControlService'
import { CreateQcTestDefinitionPayload, QcTestDefinitionFormValues, ScoreParameterDto } from '../types'
import QualityTestDefinitionForm from './components/QualityTestDefinitionForm'
import { useCreateTestDefinition } from './services/QualityControlHooks'
import { useCurrentSite } from '../store/store.hooks'

const DEFAULT_FORM_JSON = JSON.stringify({ pages: [{ elements: [] }] }, null, 2)

const QualityTestCreator = () => {
  const { t } = useTranslation()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [scoreParameters, setScoreParameters] = useState<ScoreParameterDto[]>([])
  const currentSite = useCurrentSite()

  const { data: scoreCalculatorTypes } = useSuspenseQuery({
    queryKey: ['quality-tests', 'score-calculator-types'],
    queryFn: () => fetchScoreCalculatorTypes(currentSite?.id ?? ''),
  })

  const { mutate: createTest, isPending } = useCreateTestDefinition(setErrorMsg)

  const defaultValues: QcTestDefinitionFormValues = {
    displayName: '',
    category: '',
    description: '',
    displayOrder: '',
    canTestMultipleEntities: false,
    scoreCalculatorType: scoreCalculatorTypes[0] ?? '',
    formJson: DEFAULT_FORM_JSON,
    scheduledActivationDate: '',
    targetTestsPerShift: '',
  }

  const handleSubmit = (values: QcTestDefinitionFormValues & { scoreParameters: ScoreParameterDto[] }) => {
    setErrorMsg(null)
    const payload: CreateQcTestDefinitionPayload = {
      displayName: values.displayName,
      category: values.category || undefined,
      description: values.description || undefined,
      displayOrder: values.displayOrder && Number(values.displayOrder) >= 0 ? Number(values.displayOrder) : undefined,
      form: values.formJson,
      canTestMultipleEntities: values.canTestMultipleEntities,
      scoreCalculatorType: values.scoreCalculatorType,
      scoreParameters: values.scoreParameters.length > 0 ? values.scoreParameters : undefined,
      targetTestsPerShift: values.targetTestsPerShift ? Number(values.targetTestsPerShift) : undefined,
    }
    createTest(payload)
  }

  return (
    <QualityTestDefinitionForm
      defaultValues={defaultValues}
      scoreCalculatorTypes={scoreCalculatorTypes}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      submitLabel={t('qualityControl.createTest')}
      actionError={errorMsg}
      scoreParametersProps={{ parameters: scoreParameters, onChange: setScoreParameters }}
    />
  )
}

export default QualityTestCreator
